import { useState, useCallback, useRef } from 'react';

const SAMPLE_RATE = 16000;

export const useVoiceCall = (wsUrl) => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [audioData, setAudioData] = useState(new Array(24).fill(0));
  const [messages, setMessages] = useState([]);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const wsRef = useRef(null);
  const workletNodeRef = useRef(null);
  const nextStartTimeRef = useRef(0);
  const lastBotTextRef = useRef('');
  const lastClientTextRef = useRef('');

  const playBuffer = useCallback(async (arrayBuffer) => {
    if (!audioContextRef.current) return;

    try {
      const int16Array = new Int16Array(arrayBuffer);
      const float32Array = new Float32Array(int16Array.length);
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 0x7fff;
      }

      const buffer = audioContextRef.current.createBuffer(1, float32Array.length, SAMPLE_RATE);
      buffer.getChannelData(0).set(float32Array);

      const source = audioContextRef.current.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContextRef.current.destination);

      const now = audioContextRef.current.currentTime;
      if (nextStartTimeRef.current < now) {
        nextStartTimeRef.current = now + 0.05;
      }

      source.start(nextStartTimeRef.current);
      nextStartTimeRef.current += buffer.duration;
    } catch (e) {
      console.error('Playback error:', e);
    }
  }, []);

  const endCall = useCallback(() => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, 'Call ended by user');
      }
      wsRef.current = null;
    }

    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(console.error);
      }
      audioContextRef.current = null;
    }

    nextStartTimeRef.current = 0;
    setIsCallActive(false);
    setIsConnecting(false);
    setError(null);
    setAudioData(new Array(24).fill(0));
    setMessages([]);
    lastBotTextRef.current = '';
    lastClientTextRef.current = '';
  }, []);

  const startCall = useCallback(async () => {
    if (isCallActive || isConnecting || !wsUrl) return;
    setIsConnecting(true);
    setError(null);

    try {
      if (!window.isSecureContext && window.location.hostname !== 'localhost') {
        throw new Error('InsecureContext');
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('NotSupported');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContextClass({
        sampleRate: SAMPLE_RATE,
        latencyHint: 'interactive'
      });
      audioContextRef.current = audioContext;

      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      await audioContext.audioWorklet.addModule('/audio-processor.js');

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 64;
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        const workletNode = new AudioWorkletNode(audioContext, 'pcm-processor');
        workletNodeRef.current = workletNode;

        source.connect(workletNode);
        workletNode.connect(audioContext.destination);

        workletNode.port.onmessage = (event) => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(event.data);
          }
        };

        setIsConnecting(false);
        setIsCallActive(true);
      };

      ws.onmessage = async (event) => {
        if (event.data instanceof Blob) {
          const arrayBuffer = await event.data.arrayBuffer();
          playBuffer(arrayBuffer);
        } else if (typeof event.data === 'string') {
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'streamAudio') {
              if (message.data?.audioData) {
                const binaryString = atob(message.data.audioData);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                playBuffer(bytes.buffer);
              }

              const newMessages = [];

              // ✅ 1. Handle client message FIRST
              if (message.data?.client_text) {
                if (message.data.client_text !== lastClientTextRef.current) {
                  lastClientTextRef.current = message.data.client_text;

                  newMessages.push({
                    id: Math.random().toString(36).slice(2, 11),
                    text: message.data.client_text,
                    sender: 'user',
                    timestamp: Date.now()
                  });
                }
              }

              // ✅ 2. Handle bot message SECOND
              if (message.data?.bot_text) {
                if (message.data.bot_text !== lastBotTextRef.current) {
                  lastBotTextRef.current = message.data.bot_text;

                  newMessages.push({
                    id: Math.random().toString(36).slice(2, 11),
                    text: message.data.bot_text,
                    sender: 'bot',
                    timestamp: Date.now()
                  });
                }
              }

              // ✅ 3. Add messages in correct order
              if (newMessages.length > 0) {
                setMessages((prev) => {
                  const updated = [...prev];

                  newMessages.forEach((msg) => {
                    const lastMsg = updated[updated.length - 1];

                    if (!lastMsg || lastMsg.text !== msg.text || lastMsg.sender !== msg.sender) {
                      updated.push(msg);
                    }
                  });

                  return updated;
                });
              }
            }
          } catch (e) {
            console.error('Error parsing server message:', e);
          }
        }
      };

      ws.onerror = (e) => {
        console.error('WebSocket error:', e);
        setError('Network error occurred.');
        endCall();
      };

      ws.onclose = (event) => {
        setIsCallActive(false);
      };

      const updateAudioData = () => {
        if (!analyserRef.current) return;
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        const normalizedData = Array.from(dataArray.slice(0, 24)).map((v) => v / 255);
        setAudioData(normalizedData);
        animationFrameRef.current = requestAnimationFrame(updateAudioData);
      };

      updateAudioData();
    } catch (err) {
      console.error('Modern audio engine failure:', err);
      setIsConnecting(false);
      setIsCallActive(false);

      if (err.message === 'InsecureContext') {
        setError('Voice calls require a secure connection (HTTPS).');
      } else if (err.message === 'NotSupported') {
        setError('Your browser does not support voice calls.');
      } else {
        setError('Microphone access was denied or an error occurred.');
      }
    }
  }, [isCallActive, isConnecting, wsUrl, endCall, playBuffer]);

  return {
    isCallActive,
    isConnecting,
    error,
    startCall,
    endCall,
    audioData,
    messages
  };
};
